import React, { useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, IconButton, InputLabel, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Select } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { DataTypes } from "../../../../constants";
import rmsActions from "../../../../actions/rmsActions";
import { getDevelopmentNeeds, isFetching } from "../../../../reducers";
import MenuItem from "@mui/material/MenuItem";
import AlertMessage from "../../../Common/Alert/AlertMessage";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { Link } from "react-router-dom";

function AttachDevelopmentNeedFormDialog({open, attached, developmentNeeds, onDelete, onSubmit, onSelect, onClose, canUpdate, ...props}) {
    const canSubmit = attached.length + developmentNeeds.length > 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth={true}
            maxWidth="md"
            {...props}
        >
            <DialogTitle>Linked development needs</DialogTitle>
            <DialogContent dividers={false}>
                <Box
                    component="form"
                    autoComplete="off"
                    noValidate
                    className="dialog-content"
                    onSubmit={(e) => e.preventDefault()}
                >
                    <List dense={true}>
                        {Array.isArray(attached) &&
                            attached.map((item) => (
                                <ListItem
                                    key={item.id}
                                    secondaryAction={
                                        canUpdate && (
                                            <IconButton
                                                aria-label="Delete"
                                                size="sm"
                                                onClick={() => onDelete(item.id)}
                                            >
                                                <DeleteOutlinedIcon />
                                            </IconButton>
                                        )
                                    }
                                >
                                    <ListItemButton
                                        component={Link}
                                        to={`/portfolio/developmentneeds/${item.id}`}
                                    >
                                        <ListItemIcon>
                                            <AutoGraphIcon fontSize={"small"} color={"primary"} />
                                        </ListItemIcon>
                                        <ListItemText primary={item.title} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                    </List>

                    {canUpdate && (
                        <Box className="form-row">
                            <FormControl
                                component="div"
                                fullWidth={true}
                                autoFocus
                            >
                                {developmentNeeds?.length > 0 ? (
                                    <>
                                        <InputLabel
                                            id="development-need-label"
                                            sx={{backgroundColor: "#FFF", padding: "0 4px"}}
                                        >
                                            Development need
                                        </InputLabel>
                                        <Select
                                            id="select-development-need"
                                            value=""
                                            onChange={({target}) => onSelect(target.value)}
                                        >
                                            {developmentNeeds.map((option) => (
                                                <MenuItem value={option.id} key={option.id} >
                                                    {option.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            Select a development need
                                        </FormHelperText>
                                    </>
                                ) : (
                                    <AlertMessage
                                        severity={"info"}
                                        message={"Development need selection empty. Try adding more development needs."}
                                    />
                                )}
                            </FormControl>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>
                {canUpdate && canSubmit && (
                    <Button
                        key="save"
                        onClick={onSubmit}
                        variant="contained"
                        disableElevation={true}
                        color="primary"
                    >
                        Save
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

function AttachDevelopmentNeedFormDialogContainer({formTitle, onSave, onClose, data = {}, canUpdate = false, appCode, ...props}) {
    const dispatch = useDispatch();

    const fetching = useSelector((state) => isFetching(state, DataTypes.DEVELOPMENT_NEEDS));
    let developmentNeeds = useSelector((state) => getDevelopmentNeeds(state));

    const attached = data?.developmentNeeds ?? [];

    if (!fetching && developmentNeeds === null) {
        dispatch(rmsActions.getDevelopmentNeeds(appCode));
        developmentNeeds = [];
    }

    const [tempAttached, setTempAttached] = useState(attached);
    const tempAttachedIds = tempAttached.map(({ id }) => Number(id));

    const onSelect = (id) => {
        let newDevelopmentNeed = developmentNeeds.find((newDevelopmentNeed) => Number(newDevelopmentNeed.id) === Number(id));
        setTempAttached([...tempAttached, newDevelopmentNeed]);
    };

    const onDelete = (id) => {
        let filtered = tempAttached.filter((item) => Number(item.id) !== Number(id));
        setTempAttached(filtered);
    };

    const onSubmit = () => {
        onSave(tempAttached);
        onClose();
    };

    return (
        <AttachDevelopmentNeedFormDialog
            developmentNeeds={!developmentNeeds ? [] : developmentNeeds.filter(({id}) => !tempAttachedIds.includes(Number(id)))}
            attached={tempAttached}
            onSelect={onSelect}
            onDelete={onDelete}
            onSubmit={onSubmit}
            onClose={onClose}
            canUpdate={canUpdate}
            scroll="paper"
            {...props}
        />
    );
}

export default AttachDevelopmentNeedFormDialogContainer;
